import React, {
  useState,
  lazy,
  Suspense,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { toast } from 'react-hot-toast';
import Layout from '@components/Layout';
import ImageUploader from '@components/ImageUploader';
import Modal from '@/components/Modal';
import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';
import ConnectionRequestButton from '@components/ConnectionRequestButton';

import Tour from '@components/Tour';
import { profileTourSteps } from '@utils/TourSteps/Profile';

// Address Imports
import useAddressState from '@hooks/useAddressState';
import useSaveUserAddress from '@hooks/useSaveUserAddress';
import AddressEditor from '@components/AddressEditor';

import {
  ProfileMenuItems,
  ProfileMenuParticipantItems,
} from '@/config/profileMenuConfig';

import {
  doc,
  updateDoc,
  where,
  collection,
  onSnapshot,
  query,
  getDoc,
} from 'firebase/firestore';
import { db } from '@root/firebase';

// CSS
import '@css/custom/dropzone/dropzone.css';

// JS
import 'https://unpkg.com/dropzone@5/dist/min/dropzone.min.js';

const background =
  'https://firebasestorage.googleapis.com/v0/b/smartdog-bbe9f.firebasestorage.app/o/images%2Fpattern-bg.jpg?alt=media&token=14701df7-af49-4a07-8a69-d45c8c23a8ef';

function Profile() {
  const { uid: routeUid } = useParams();
  const { globalUser, globalData, setGlobalData, defaultAvatarUrl, LogError } =
    useAuth();

  /* -------------------------------------------------------
   * Decide who we're viewing FIRST (so downstream hooks can use it)
   * ----------------------------------------------------- */
  const viewingSelf = !routeUid || routeUid === globalUser?.uid;
  const profileUid = viewingSelf ? globalUser?.uid : routeUid;

  // Connection state for viewed profile
  const [isFriend, setIsFriend] = useState(false);
  const [isPending, setIsPending] = useState(false);

  // Local state for counts against the *profile being viewed*
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [dogsCount, setDogsCount] = useState(0);

  // When viewing another user, fetch their user doc for display
  const [targetData, setTargetData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(!viewingSelf);

  /* ---------------- Connections live state ---------------- */
  useEffect(() => {
    if (!globalUser?.uid || !profileUid || viewingSelf) return;
    const ref = doc(db, `users/${globalUser.uid}/connections/${profileUid}`);
    const unsub = onSnapshot(ref, (snap) => setIsFriend(snap.exists()));
    return () => unsub();
  }, [globalUser?.uid, profileUid, viewingSelf]);

  useEffect(() => {
    if (!globalUser?.uid || !profileUid || viewingSelf) return;
    // Outgoing request tracked on the receiver's notifications
    const notifRef = doc(
      db,
      `users/${profileUid}/notifications/${globalUser.uid}`
    );
    const unsub = onSnapshot(notifRef, (snap) => {
      const data = snap.data();
      setIsPending(!!data && data.status === 'pending');
    });
    return () => unsub();
  }, [globalUser?.uid, profileUid, viewingSelf]);

  /* ---------------- Load profile doc (if viewing others) ---------------- */
  useEffect(() => {
    if (viewingSelf) {
      setTargetData(globalData || null);
      setLoadingProfile(false);
      return;
    }
    if (!profileUid) return;

    (async () => {
      setLoadingProfile(true);
      try {
        const userRef = doc(db, 'users', profileUid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          toast.error('Profile not found.');
          setTargetData(null);
          return;
        }
        setTargetData({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.error('Failed to load target profile', e);
        toast.error('Failed to load profile.');
      } finally {
        setLoadingProfile(false);
      }
    })();
  }, [viewingSelf, profileUid, globalData]);

  /* ---------------- Counts (connections / dogs) ---------------- */
  useEffect(() => {
    if (!profileUid) return;
    const ref = collection(db, `users/${profileUid}/connections`);
    const unsub = onSnapshot(
      ref,
      (snap) => setConnectionsCount(snap.size),
      (err) => console.error('connections count error', err)
    );
    return () => unsub();
  }, [profileUid]);

  useEffect(() => {
    if (!profileUid) return;

    const dogsTopLevelQ = query(
      collection(db, 'dogs'),
      where('ownerId', '==', profileUid)
    );
    const dogsSubRef = collection(db, `users/${profileUid}/dogs`);

    let topLevelCount = 0;
    let subCount = 0;

    const unsubTop = onSnapshot(
      dogsTopLevelQ,
      (snap) => {
        topLevelCount = snap.size;
        setDogsCount(topLevelCount > 0 ? topLevelCount : subCount);
      },
      (err) => console.error('dogs (top-level) count error', err)
    );

    const unsubSub = onSnapshot(
      dogsSubRef,
      (snap) => {
        subCount = snap.size;
        setDogsCount(topLevelCount > 0 ? topLevelCount : subCount);
      },
      (err) => console.error('dogs (subcollection) count error', err)
    );

    return () => {
      unsubTop();
      unsubSub();
    };
  }, [profileUid]);

  const imageUploaderRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  const [activeTab, setActiveTab] = useState('Overview'); // default for self
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(
    globalData?.name || 'Guest User'
  );

  const [uploadState, setUploadState] = useState({
    avatar: { loading: false, progress: 0 },
    background: { loading: false, progress: 0 },
  });
  const updateUploadState = (type, updates) => {
    setUploadState((prev) => ({
      ...prev,
      [type]: { ...prev[type], ...updates },
    }));
  };

  // Address logic (self only)
  const {
    addrQuery,
    setAddrQuery,
    addressFieldsState,
    handleAddressChange,
    applyTomTomPlace,
  } = useAddressState(globalData);

  const { saveAddress, isSaving: isSavingAddress } = useSaveUserAddress({
    db,
    uid: globalUser?.uid,
    globalData,
    setGlobalData,
    toast,
    LogError,
  });

  // Decide which dataset to render (self vs target)
  const displayData = useMemo(() => {
    return viewingSelf ? globalData : targetData;
  }, [viewingSelf, globalData, targetData]);

  // Build the tabs menu for the profile being viewed
  const baseMenu =
    (displayData?.accountType === 'User'
      ? ProfileMenuItems
      : ProfileMenuParticipantItems) || [];

  // For other users: hide "Overview"
  const publicMenu = useMemo(() => {
    if (viewingSelf) return baseMenu;
    return baseMenu.filter((m) => m.name?.toLowerCase() !== 'overview');
  }, [baseMenu, viewingSelf]);

  // Keep ActiveTab valid if the set of tabs changes
  useEffect(() => {
    if (viewingSelf) return;
    if (!publicMenu.some((m) => m.name === activeTab)) {
      setActiveTab(publicMenu[0]?.name || '');
    }
  }, [publicMenu, viewingSelf]); // eslint-disable-line

  // Resolve tab component (supports dynamic import OR direct component)
  const rawComp = useMemo(
    () => publicMenu.find((item) => item.name === activeTab)?.component,
    [publicMenu, activeTab]
  );

  const ResolvedTabComponent = useMemo(() => {
    if (!rawComp) return null;
    try {
      const src = rawComp.toString();
      if (src.includes('import(')) {
        return lazy(rawComp);
      }
      return rawComp;
    } catch {
      return rawComp;
    }
  }, [rawComp]);

  /* ---------------- image uploads (self only) ---------------- */
  const handleUploadAvatar = async () => {
    if (!imageUploaderRef.current) {
      toast.error('ImageUploader is not ready.');
      return;
    }
    updateUploadState('avatar', { loading: true, progress: 0 });
    try {
      await imageUploaderRef.current.handleUpload(
        globalUser.uid,
        'avatar.jpg',
        'avatar',
        `users`,
        (progress) => updateUploadState('avatar', { progress })
      );
    } catch (error) {
      toast.error('Failed to upload avatar.');
    } finally {
      updateUploadState('avatar', { loading: false, progress: 0 });
    }
  };

  const handleUploadBackground = async () => {
    if (!imageUploaderRef.current) {
      toast.error('ImageUploader is not ready.');
      return;
    }
    updateUploadState('background', { loading: true, progress: 0 });
    try {
      await imageUploaderRef.current.handleUpload(
        globalUser.uid,
        'backgroundImage.jpg',
        'backgroundImage',
        `users`,
        (progress) => updateUploadState('background', { progress })
      );
    } catch (error) {
      toast.error('Failed to upload background.');
    } finally {
      updateUploadState('background', { loading: false, progress: 0 });
    }
  };

  const handleNameSave = async () => {
    if (!editedName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      const userDocRef = doc(db, 'users', globalUser.uid);
      await updateDoc(userDocRef, { name: editedName });
      setGlobalData((prev) => ({ ...prev, name: editedName }));
      toast.success('Name updated successfully!');
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating name in Firestore:', error);
      LogError('Error', {
        error: error,
        userName: globalData?.name || 'No Name',
        uid: globalUser?.uid || 'No ID',
        functionCode: 'handleNameSave',
        page: 'SMART Profile',
      });
      toast.error('Failed to update name');
    }
  };

  return (
    <Layout>
      {loadingProfile && !viewingSelf ? (
        <div className='card p-4'>Loading profile…</div>
      ) : (
        <>
          <div className='row page-titles'>
            <div className='col-lg-3'>
              <ol className='breadcrumb'>
                {viewingSelf ? (
                  <>
                    <li className='breadcrumb-item active'>My Account</li>
                    <li className='breadcrumb-item'>My Profile</li>
                  </>
                ) : (
                  <>
                    <li className='breadcrumb-item active'>User Profile</li>
                    <li className='breadcrumb-item'>
                      {displayData?.name || 'Loading…'}
                    </li>
                  </>
                )}
              </ol>
            </div>
            {viewingSelf && (
              <div className='col-lg-4'>
                <Tour steps={profileTourSteps} pageKey='profile' />
              </div>
            )}
          </div>

          <div className='row'>
            <div className='col-lg-12'>
              <div className='profile card card-body px-3 pt-3 pb-0'>
                <div className='profile-head'>
                  <div className='photo-content'>
                    <div className='cover-photo rounded'>
                      <img
                        src={displayData?.backgroundImage || background}
                        className='profile-img profile-foreground-img rounded-top rounded-bottom'
                        style={{
                          width: '100%',
                          height: '300px',
                          objectFit: 'cover',
                        }}
                        alt='Background'
                      />
                    </div>
                  </div>
                  <div className='profile-info'>
                    <div className='profile-photo'>
                      <img
                        src={displayData?.avatar || defaultAvatarUrl}
                        className='img-fluid rounded-circle'
                        alt=''
                      />
                    </div>
                    <div className='profile-details'>
                      <div className='profile-name px-3 pt-2'>
                        <div className='text-primary mb-0'>
                          {/* Name (editable only for self) */}
                          {viewingSelf ? (
                            isEditingName ? (
                              <div className='d-flex align-items-center'>
                                <input
                                  type='text'
                                  value={editedName}
                                  onChange={(e) =>
                                    setEditedName(e.target.value)
                                  }
                                  className='form-control form-control-sm'
                                  style={{
                                    maxWidth: '200px',
                                    marginRight: '10px',
                                  }}
                                />
                                <button
                                  className='btn btn-success btn-sm'
                                  onClick={handleNameSave}
                                >
                                  Save
                                </button>
                                <button
                                  className='btn btn-secondary btn-sm ms-2'
                                  onClick={() => setIsEditingName(false)}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className='d-flex align-items-center'>
                                <h4 className='mb-1'>
                                  {displayData?.name || 'User'}
                                </h4>
                                <i
                                  className='fas fa-pencil-alt fa-xs ms-2'
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    setEditedName(globalData?.name || '');
                                    setIsEditingName(true);
                                  }}
                                ></i>
                              </div>
                            )
                          ) : (
                            <h4 className='mb-1'>
                              {displayData?.name || 'User'}
                            </h4>
                          )}
                        </div>
                        <p>#{displayData?.membershipNumber}</p>
                      </div>

                      <div className='profile-email px-2 pt-2'>
                        {viewingSelf && (
                          <h4 className='text-muted mb-0'>
                            {displayData?.email || ''}
                          </h4>
                        )}

                        {viewingSelf && (
                          <p>
                            {globalData?.address?.[0]?.city},{' '}
                            {globalData?.address?.[0]?.country}
                          </p>
                        )}
                      </div>

                      {/* Actions dropdown & modals only for self */}
                      {viewingSelf && (
                        <div className='dropdown ms-auto'>
                          <div className='dropdown custom-dropdown'>
                            <button
                              type='button'
                              className='btn btn-sm btn-primary'
                              data-bs-toggle='dropdown'
                              aria-expanded='false'
                            >
                              <i className='fa fa-pencil-alt'></i>
                              &nbsp;&nbsp;Actions
                              <i className='fa fa-angle-down ms-3'></i>
                            </button>
                            <div className='dropdown-menu dropdown-menu-end'>
                              <button
                                className='dropdown-item'
                                data-bs-toggle='modal'
                                data-bs-target='#editAvatarModal'
                              >
                                Change Avatar
                              </button>
                              <button
                                className='dropdown-item'
                                data-bs-toggle='modal'
                                data-bs-target='#editBackgroundModal'
                              >
                                Change Background
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Background Modal (self only) */}
                      {viewingSelf && (
                        <Modal
                          id='editBackgroundModal'
                          title='Update Background Image'
                          onConfirm={async () => {
                            setIsSaving(true);
                            try {
                              await handleUploadBackground();
                            } finally {
                              setIsSaving(false);
                            }
                          }}
                          confirmText='Upload'
                          cancelText='Cancel'
                          loading={isSaving}
                          loadingText='Uploading...'
                          size='medium'
                        >
                          <ImageUploader
                            ref={imageUploaderRef}
                            uid={globalUser.uid}
                            onUpload={(url) =>
                              setGlobalData((prev) => ({
                                ...prev,
                                backgroundImage: url,
                              }))
                            }
                          />
                        </Modal>
                      )}

                      {/* Edit Avatar Modal (self only) */}
                      {viewingSelf && (
                        <Modal
                          id='editAvatarModal'
                          title='Update Avatar'
                          onConfirm={async () => {
                            setIsSaving(true);
                            try {
                              await handleUploadAvatar();
                            } finally {
                              setIsSaving(false);
                            }
                          }}
                          confirmText='Upload'
                          cancelText='Cancel'
                          loading={isSaving}
                          loadingText='Uploading...'
                          size='medium'
                        >
                          <ImageUploader
                            ref={imageUploaderRef}
                            uid={globalUser.uid}
                            onUpload={(url) =>
                              setGlobalData((prev) => ({
                                ...prev,
                                avatar: url,
                              }))
                            }
                          />
                        </Modal>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-xl-4'>
              <div className='row'>
                {/* Stats */}
                <div className='col-xl-12'>
                  <div className='card'>
                    <div className='card-body'>
                      <div className='profile-statistics'>
                        <div className='text-center'>
                          <div className='row'>
                            <div className='col'>
                              <h3 className='m-b-0'>{connectionsCount}</h3>
                              <span>Connections</span>
                            </div>
                            <div className='col'>
                              <h3 className='m-b-0'>{dogsCount}</h3>
                              <span>Dogs</span>
                            </div>
                            <div className='col'>
                              <h3 className='m-b-0'>45</h3>
                              <span>Favorited</span>
                            </div>
                          </div>
                        </div>
                        {/* Send Message modal removed – unchanged */}
                      </div>
                    </div>
                  </div>
                </div>

                {!viewingSelf && (
                  <>
                    {/* Connection, Fav, Like */}
                    <div className='col-xl-12'>
                      <div className='card'>
                        <div className='card-body'>
                          <div className='profile-statistics'>
                            <div className='text-center'>
                              <div className='row'>
                                <div className='col'>
                                  <LikeButton
                                    collectionPath='users'
                                    documentId={displayData?.id}
                                    userId={globalData.uid}
                                    styled={true}
                                  />
                                </div>
                                <div className='col'>
                                  <FavoriteButton
                                    collectionPath={`users`}
                                    documentId={displayData?.id}
                                    userId={globalData.uid}
                                    styled={true}
                                  />
                                </div>
                                <div className='col'>
                                  <ConnectionRequestButton
                                    senderId={globalData.uid}
                                    senderName={globalData?.name}
                                    senderAvatar={globalData?.avatar}
                                    receiverId={profileUid}
                                    isFriend={isFriend}
                                    isPending={isPending}
                                    onPending={() => setIsPending(true)}
                                  />
                                </div>
                              </div>
                            </div>
                            {/* Send Message modal removed – unchanged */}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Address card */}
                <div className='col-xl-12'>
                  <div className='card'>
                    <div className='card-body'>
                      <h5 className='text-primary d-inline '>
                        Address
                        {viewingSelf && (
                          <i
                            className='fas fa-pencil-alt fa-xs ms-2'
                            style={{ cursor: 'pointer' }}
                            data-bs-toggle='modal'
                            data-bs-target='#editAddressModal'
                          ></i>
                        )}
                      </h5>
                      <div className='row mb-2 mt-2'>
                        {viewingSelf && (
                          <>
                            <div className='col-sm-4 col-5'>
                              <h5 className='f-w-500'>Street: </h5>
                            </div>
                            <div className='col-sm-8 col-7'>
                              <span>{globalData?.address?.[0]?.street}</span>
                            </div>
                          </>
                        )}

                        <div className='col-sm-4 col-5'>
                          <h5 className='f-w-500'>City:</h5>
                        </div>
                        <div className='col-sm-8 col-7'>
                          <span>{globalData?.address?.[0]?.city}</span>
                        </div>

                        <div className='col-sm-4 col-5'>
                          <h5 className='f-w-500'>Region:</h5>
                        </div>
                        <div className='col-sm-8 col-7'>
                          <span>{globalData?.address?.[0]?.region}</span>
                        </div>

                        <div className='col-sm-4 col-5'>
                          <h5 className='f-w-500'>Country:</h5>
                        </div>
                        <div className='col-sm-8 col-7'>
                          <span>{globalData?.address?.[0]?.country}</span>
                        </div>
                        {viewingSelf && (
                          <>
                            <div className='col-sm-4 col-5'>
                              <h5 className='f-w-500'>Zip: </h5>
                            </div>

                            <div className='col-sm-8 col-7'>
                              <span>{globalData?.address?.[0]?.zip}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* END Address card */}
              </div>
            </div>

            {!viewingSelf && (
              <div className='col-xl-8'>
                <div className='card'>
                  <div className='card-body'>
                    <h4>About {displayData?.name || 'User'}</h4>
                    <hr />
                    {displayData?.about || 'About Information unavailable'}
                  </div>
                </div>
              </div>
            )}

            {/* Tabs (Overview removed for others) */}
            {viewingSelf && (
              <div className='col-xl-8'>
                <div className='card'>
                  <div className='card-body'>
                    <div className='profile-tab'>
                      <div className='custom-tab-1'>
                        <ul className='nav nav-tabs'>
                          {publicMenu.map((item, index) => (
                            <li className='nav-item' key={index}>
                              <button
                                type='button'
                                className={`nav-link ${
                                  activeTab === item.name ? 'active' : ''
                                }`}
                                onClick={() => setActiveTab(item.name)}
                              >
                                {item.name}
                              </button>
                            </li>
                          ))}
                        </ul>

                        <div className='tab-content p-3'>
                          {ResolvedTabComponent ? (
                            <Suspense fallback={<div>Loading...</div>}>
                              <ResolvedTabComponent
                                globalUser={globalUser}
                                globalData={displayData}
                                setGlobalData={
                                  viewingSelf ? setGlobalData : undefined
                                }
                                profileMenu={[
                                  {
                                    name: 'Membership #',
                                    value: displayData?.membershipNumber,
                                    span: 'badge rounded-pill bg-success',
                                  },
                                  {
                                    name: 'Joined Date',
                                    value: displayData?.joinedDate,
                                  },
                                  { name: 'Plan', value: '' },
                                  { name: 'Start Date', value: '' },
                                  { name: 'Next Payment', value: '' },
                                  { name: 'Frequency', value: '' },
                                ]}
                                LogError={LogError}
                                readOnly={!viewingSelf}
                              />
                            </Suspense>
                          ) : (
                            <div>No Component Found</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Tabs */}
          </div>

          {/* Address Modal (self only) */}
          {viewingSelf && (
            <Modal
              id='editAddressModal'
              title='Edit Your Address'
              onConfirm={async () => {
                // 🔎 Debug: show everything we're about to save
                try {
                  // Pretty console grouping
                  console.groupCollapsed('[Address Save] Debug payload');
                  console.log('addrQuery:', addrQuery);
                  console.table({
                    street: addressFieldsState?.street || '',
                    city: addressFieldsState?.city || '',
                    region: addressFieldsState?.region || '',
                    country: addressFieldsState?.country || '',
                    zip: addressFieldsState?.zip || '',
                    id: addressFieldsState?.id ?? null,
                  });
                  console.log('currentUserUid:', globalUser?.uid);
                  console.log('globalData.name:', globalData?.name);
                  console.groupEnd();
                } catch (e) {
                  // never block saving due to logging errors
                  console.warn('Address debug logging failed:', e);
                }

                // proceed with your existing save
                await saveAddress(addressFieldsState);
              }}
              confirmText='Update'
              cancelText='Cancel'
              loading={isSavingAddress}
              loadingText='Updating...'
              size='lg'
            >
              <AddressEditor
                addrQuery={addrQuery}
                setAddrQuery={setAddrQuery}
                addressFieldsState={addressFieldsState}
                handleAddressChange={handleAddressChange}
                applyTomTomPlace={applyTomTomPlace}
              />
            </Modal>
          )}
        </>
      )}
    </Layout>
  );
}

export default Profile;
